import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Play, Search, CheckCircle, AlertTriangle, XCircle, ArrowLeft } from 'lucide-react';

import { v3Api } from '../hooks/useApi';
import { formatMinor, formatMoneyExact } from '../Shared/Formatters';
import { HoldoverPill } from './parts/StatusPill';
import './invoicing.css';

/**
 * Generate wizard: pick a period -> review what would be billed -> commit.
 *
 * The dry run is not optional and not a checkbox — it is step 2, and the commit
 * button only appears after it has been seen. With the approval workflow parked
 * this is the only review gate before numbered invoices exist, so it cannot be
 * something a user clicks past.
 */

const STEPS = ['period', 'review', 'done'];

export default function InvoiceGenerate() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [step, setStep] = useState('period');
    const [periodKey, setPeriodKey] = useState(() => defaultPeriod());
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [run, setRun] = useState(null);
    const [conflictPolicy, setConflictPolicy] = useState('skip');
    // Which tenants to actually bill. Seeded to everything billable when the
    // dry run returns, so the default stays "the whole period" — deselecting is
    // the deliberate act, not selecting.
    const [selected, setSelected] = useState(() => new Set());
    const [tenantFilter, setTenantFilter] = useState('');

    // Session nonce, so two separate visits to this page can legitimately run
    // the same selection twice rather than the second replaying the first.
    const [sessionNonce] = useState(() => Math.random().toString(36).slice(2, 10));

    const runPreview = useCallback(() => {
        if (!periodKey) return;
        setBusy(true);
        v3Api.invoicing.preview({ period_key: periodKey })
            .then((res) => {
                setPreview(res);
                setSelected(new Set(
                    (res.rows || [])
                        .filter((r) => r.eligible && r.total_minor > 0)
                        .map((r) => String(r.tenant_id)),
                ));
                setTenantFilter('');
                setStep('review');
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Preview failed')))
            .finally(() => setBusy(false));
    }, [periodKey, t]);

    // ── derived state ─────────────────────────────────────────────────────
    // Declared BEFORE commit() so the callback does not close over a binding
    // that is still in its temporal dead zone at definition time.
    const billable = useMemo(
        () => (preview?.rows || []).filter((r) => r.eligible && r.total_minor > 0),
        [preview],
    );
    const skipped = useMemo(
        () => (preview?.rows || []).filter((r) => !r.eligible || r.total_minor === 0),
        [preview],
    );
    const holdovers = useMemo(() => billable.filter((r) => r.is_holdover), [billable]);
    const currency = billable[0]?.currency || 'AED';

    // Rows the tenant filter is currently showing. Filtering is display-only —
    // it must never change what is selected, or typing in the search box would
    // silently drop tenants from the run.
    const visible = useMemo(() => {
        const q = tenantFilter.trim().toLowerCase();
        if (!q) return billable;
        return billable.filter((r) => (
            (r.store_name || '').toLowerCase().includes(q)
            || (r.tenant_code || '').toLowerCase().includes(q)
            || (r.unit_code || '').toLowerCase().includes(q)
        ));
    }, [billable, tenantFilter]);

    const selectedRows = useMemo(
        () => billable.filter((r) => selected.has(String(r.tenant_id))),
        [billable, selected],
    );
    const selectedIsSubset = selectedRows.length !== billable.length;

    const commit = useCallback(() => {
        setBusy(true);
        const payload = { period_key: periodKey, conflict_policy: conflictPolicy };
        // Only scope the request when it is actually a subset. Sending the full
        // id list when everything is selected would silently drop any tenant
        // that became eligible between the dry run and the commit.
        if (selectedIsSubset) payload.tenant_ids = [...selected];

        // The idempotency key must DERIVE FROM THE PAYLOAD. A key fixed for the
        // life of the component would make a second commit with a different
        // selection replay the first run's report and silently generate
        // nothing. Sorting the ids keeps the key stable under reordering, so a
        // genuine double-click still collapses into one batch.
        const scope = selectedIsSubset ? [...selected].sort().join(',') : 'all';
        const idemKey = `ui-${sessionNonce}-${periodKey}-${conflictPolicy}-${scope}`;

        v3Api.invoicing.generate(payload, idemKey)
            .then((res) => {
                setRun(res);
                setStep('done');
                const n = (res.counts?.created || 0) + (res.counts?.replaced || 0);
                if (n) toast.success(t('{{count}} invoice(s) generated.', { count: n }));
                else toast.info(t('Nothing new to generate — every eligible tenant already has an invoice for this period.'));
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Generation failed')))
            .finally(() => setBusy(false));
    }, [periodKey, conflictPolicy, sessionNonce, selected, selectedIsSubset, t]);

    // Totals follow the SELECTION, not the full period — the number on screen
    // must be the number about to be billed.
    const grandTotal = useMemo(
        () => selectedRows.reduce((a, r) => a + (r.total_minor || 0), 0),
        [selectedRows],
    );
    const selectedHoldovers = useMemo(
        () => selectedRows.filter((r) => r.is_holdover).length,
        [selectedRows],
    );

    const toggle = (tenantId) => {
        const key = String(tenantId);
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Acts on the VISIBLE rows only, so "select all" under an active filter
    // means "all of these", which is what the checkbox appears to promise.
    const setAllVisible = (on) => {
        setSelected((prev) => {
            const next = new Set(prev);
            visible.forEach((r) => {
                if (on) next.add(String(r.tenant_id)); else next.delete(String(r.tenant_id));
            });
            return next;
        });
    };

    const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(String(r.tenant_id)));

    return (
        <div className="v3 v3-inv">
            <div className="v3-inv-header">
                <div>
                    <h1 className="v3-inv-title">{t('Generate Invoices')}</h1>
                    <p className="v3-inv-sub">
                        {t('Invoices are recomputed from lease terms and recorded sales. Review the dry run before committing.')}
                    </p>
                </div>
                <div className="v3-inv-actions">
                    <button
                        type="button"
                        className="v3-inv-btn"
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing`)}
                    >
                        <ArrowLeft size={14} /> {t('Back to invoices')}
                    </button>
                </div>
            </div>

            <div className="v3-inv-steps">
                {STEPS.map((s, i) => (
                    <span
                        key={s}
                        className={`v3-inv-step ${step === s ? 'active' : ''} ${STEPS.indexOf(step) > i ? 'done' : ''}`}
                    >
                        {i + 1}. {{ period: t('Choose period'), review: t('Review'), done: t('Done') }[s]}
                    </span>
                ))}
            </div>

            {/* ── step 1 ─────────────────────────────────────────────────── */}
            {step === 'period' && (
                <div className="v3-inv-card">
                    <h2 className="v3-inv-card-title">{t('Billing period')}</h2>
                    <div className="v3-inv-filters">
                        <div className="v3-inv-field">
                            <span className="v3-inv-label">{t('Month')}</span>
                            <input
                                className="v3-inv-input"
                                type="month"
                                value={periodKey}
                                onChange={(e) => setPeriodKey(e.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="v3-inv-btn v3-inv-btn-primary"
                            onClick={runPreview}
                            disabled={busy || !periodKey}
                        >
                            <Search size={14} /> {busy ? t('Calculating…') : t('Run dry run')}
                        </button>
                    </div>
                    <div className="v3-inv-banner v3-inv-banner-info">
                        <span>
                            {t('The dry run writes nothing. It shows exactly what each tenant would be billed, so figures can be checked before any invoice number is issued.')}
                        </span>
                    </div>
                </div>
            )}

            {/* ── step 2 ─────────────────────────────────────────────────── */}
            {step === 'review' && preview && (
                <>
                    <div className="v3-inv-kpis">
                        <div className="v3-inv-kpi">
                            <div className="v3-inv-kpi-label">{t('Selected')}</div>
                            <div className="v3-inv-kpi-value">
                                {selectedRows.length}
                                <span className="v3-inv-muted" style={{ fontSize: 13, fontWeight: 400 }}> / {billable.length}</span>
                            </div>
                        </div>
                        <div className="v3-inv-kpi">
                            <div className="v3-inv-kpi-label">{t('Skipped')}</div>
                            <div className="v3-inv-kpi-value">{skipped.length}</div>
                        </div>
                        <div className="v3-inv-kpi">
                            <div className="v3-inv-kpi-label">{t('Holdover')}</div>
                            <div className="v3-inv-kpi-value">
                                {selectedHoldovers}
                                {selectedHoldovers !== holdovers.length && (
                                    <span className="v3-inv-muted" style={{ fontSize: 13, fontWeight: 400 }}> / {holdovers.length}</span>
                                )}
                            </div>
                        </div>
                        <div className="v3-inv-kpi">
                            <div className="v3-inv-kpi-label">{t('Grand total')}</div>
                            {/* Exact — this is the number finance signs off on. */}
                            <div className="v3-inv-kpi-value" style={{ fontSize: 16 }}>
                                {formatMoneyExact(grandTotal / 100, currency)}
                            </div>
                        </div>
                    </div>

                    {selectedHoldovers > 0 && (
                        <div className="v3-inv-banner v3-inv-banner-warn">
                            <AlertTriangle size={16} />
                            <span>
                                {t('{{count}} tenant(s) have a lease end date in the past but are still trading. They are billed on their last known lease terms and will be withheld from automatic emailing until someone confirms the renewal.', { count: selectedHoldovers })}
                            </span>
                        </div>
                    )}

                    {selectedIsSubset && (
                        <div className="v3-inv-banner v3-inv-banner-info">
                            <span>
                                {t('Generating for {{count}} of {{all}} eligible tenants. The rest keep no invoice for this period and can be generated later — rerunning is safe, existing invoices are skipped.', { count: selectedRows.length, all: billable.length })}
                            </span>
                        </div>
                    )}

                    <div className="v3-inv-banner v3-inv-banner-info">
                        <span>
                            {t('Totals are recomputed from raw sales and will not match the Revenue Share Report. Each invoice carries the variance so the difference can be explained.')}
                        </span>
                    </div>

                    <div className="v3-inv-card">
                        <h2 className="v3-inv-card-title">
                            {t('Will be billed')} ({selectedRows.length} {t('of')} {billable.length})
                        </h2>

                        {/* Per-tenant selection. Everything starts ticked, so the
                            default is still a whole-period run — untick to bill a
                            subset, or filter to one tenant and bill just them. */}
                        <div className="v3-inv-filters">
                            <div className="v3-inv-field" style={{ minWidth: 240 }}>
                                <span className="v3-inv-label">{t('Find tenant')}</span>
                                <input
                                    className="v3-inv-input"
                                    type="text"
                                    value={tenantFilter}
                                    onChange={(e) => setTenantFilter(e.target.value)}
                                    placeholder={t('Name, code or unit…')}
                                />
                            </div>
                            <button type="button" className="v3-inv-btn" onClick={() => setAllVisible(true)}>
                                {tenantFilter ? t('Select these') : t('Select all')}
                            </button>
                            <button type="button" className="v3-inv-btn" onClick={() => setAllVisible(false)}>
                                {tenantFilter ? t('Clear these') : t('Clear all')}
                            </button>
                            {tenantFilter && visible.length === 1 && (
                                <button
                                    type="button"
                                    className="v3-inv-btn"
                                    onClick={() => setSelected(new Set([String(visible[0].tenant_id)]))}
                                >
                                    {t('Only this tenant')}
                                </button>
                            )}
                        </div>

                        <div className="v3-inv-table-wrap">
                            <table className="v3-inv-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 34 }}>
                                            <input
                                                type="checkbox"
                                                checked={allVisibleSelected}
                                                onChange={(e) => setAllVisible(e.target.checked)}
                                                title={t('Select all')}
                                            />
                                        </th>
                                        <th>{t('Tenant')}</th>
                                        <th>{t('Unit')}</th>
                                        <th>{t('Method')}</th>
                                        <th className="v3-inv-num">{t('Days')}</th>
                                        <th className="v3-inv-num">{t('Net Sales')}</th>
                                        <th className="v3-inv-num">{t('Total')}</th>
                                        <th>{t('Notes')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map((r) => {
                                        const on = selected.has(String(r.tenant_id));
                                        return (
                                            <tr key={r.tenant_id} style={on ? undefined : { opacity: 0.45 }}>
                                                <td>
                                                    <input type="checkbox" checked={on} onChange={() => toggle(r.tenant_id)} />
                                                </td>
                                                <td>
                                                    {r.store_name}
                                                    <span className="v3-inv-basis">{r.tenant_code}</span>
                                                </td>
                                                <td>{r.unit_code || '—'}</td>
                                                <td>{r.method || t('Not applicable')}</td>
                                                <td className="v3-inv-num">{r.billable_days}</td>
                                                <td className="v3-inv-num">{formatMoneyExact(r.net_sales, r.currency)}</td>
                                                <td className="v3-inv-num">{formatMinor(r.total_minor, r.currency)}</td>
                                                <td>
                                                    <HoldoverPill isHoldover={r.is_holdover} expiredOn={r.lease_expired_on} compact />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="v3-inv-total-row">
                                        <td colSpan={6}>
                                            {t('Selected total')}
                                            {tenantFilter && (
                                                <span className="v3-inv-basis">
                                                    {t('Filter is showing {{shown}} of {{all}} tenants — the total covers every selected tenant, not just the visible ones.', { shown: visible.length, all: billable.length })}
                                                </span>
                                            )}
                                        </td>
                                        <td className="v3-inv-num">{formatMinor(grandTotal, currency)}</td>
                                        <td />
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {!visible.length && (
                            <div className="v3-inv-empty">{t('No tenants match that search.')}</div>
                        )}
                    </div>

                    {/* Skipped tenants are shown, never silently dropped — a batch
                        that quietly misses 60% of tenants looks identical to a
                        complete one unless the misses are on screen. */}
                    {skipped.length > 0 && (
                        <div className="v3-inv-card">
                            <h2 className="v3-inv-card-title">
                                {t('Will be skipped')} ({skipped.length})
                            </h2>
                            <div className="v3-inv-table-wrap">
                                <table className="v3-inv-table">
                                    <thead>
                                        <tr>
                                            <th>{t('Tenant')}</th>
                                            <th>{t('Unit')}</th>
                                            <th className="v3-inv-num">{t('Net Sales')}</th>
                                            <th>{t('Reason')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {skipped.map((r) => (
                                            <tr key={r.tenant_id}>
                                                <td>
                                                    {r.store_name}
                                                    <span className="v3-inv-basis">{r.tenant_code}</span>
                                                </td>
                                                <td>{r.unit_code || '—'}</td>
                                                <td className="v3-inv-num">{formatMoneyExact(r.net_sales, r.currency)}</td>
                                                <td className="v3-inv-muted">
                                                    {r.ineligible_reason
                                                        || (r.warnings || []).join(' ')
                                                        || t('Computed total is zero — nothing to bill.')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="v3-inv-card">
                        <h2 className="v3-inv-card-title">{t('Commit')}</h2>
                        <div className="v3-inv-filters">
                            <div className="v3-inv-field" style={{ minWidth: 260 }}>
                                <span className="v3-inv-label">{t('If an invoice already exists')}</span>
                                <select
                                    className="v3-inv-select"
                                    value={conflictPolicy}
                                    onChange={(e) => setConflictPolicy(e.target.value)}
                                >
                                    <option value="skip">{t('Skip it (recommended)')}</option>
                                    <option value="replace">{t('Void it and issue a new one')}</option>
                                </select>
                            </div>
                            <button type="button" className="v3-inv-btn" onClick={() => setStep('period')} disabled={busy}>
                                {t('Back')}
                            </button>
                            <button
                                type="button"
                                className="v3-inv-btn v3-inv-btn-primary"
                                onClick={commit}
                                disabled={busy || !selectedRows.length}
                            >
                                <Play size={14} />
                                {busy ? t('Generating…') : t('Generate {{count}} invoice(s)', { count: selectedRows.length })}
                            </button>
                        </div>
                        {conflictPolicy === 'replace' && (
                            <div className="v3-inv-banner v3-inv-banner-warn" style={{ marginTop: 12 }}>
                                <AlertTriangle size={16} />
                                <span>
                                    {t('Replacing voids the existing invoice and issues a new one with a NEW number. The old number is retained on the voided record and is never reused.')}
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── step 3 ─────────────────────────────────────────────────── */}
            {step === 'done' && run && (
                <div className="v3-inv-card">
                    <h2 className="v3-inv-card-title">{t('Run report')}</h2>
                    <div className="v3-inv-kpis">
                        <Kpi label={t('Created')} value={run.counts?.created || 0} />
                        <Kpi label={t('Replaced')} value={run.counts?.replaced || 0} />
                        <Kpi label={t('Skipped')} value={run.counts?.skipped || 0} />
                        <Kpi label={t('Failed')} value={run.counts?.failed || 0} />
                    </div>

                    {run.mapping_changed && (
                        <div className="v3-inv-banner v3-inv-banner-warn">
                            <AlertTriangle size={16} />
                            <span>
                                {t('The charge name mapping changed since a previous run for this period. Invoices from the two runs are not directly comparable.')}
                            </span>
                        </div>
                    )}

                    <div className="v3-inv-table-wrap">
                        <table className="v3-inv-table">
                            <thead>
                                <tr>
                                    <th>{t('Tenant')}</th>
                                    <th>{t('Outcome')}</th>
                                    <th>{t('Invoice #')}</th>
                                    <th className="v3-inv-num">{t('Total')}</th>
                                    <th>{t('Message')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(run.results || []).map((r, i) => (
                                    <tr key={`${r.tenant_id}-${i}`}>
                                        <td>
                                            {r.store_name}
                                            <span className="v3-inv-basis">{r.tenant_code}</span>
                                        </td>
                                        <td><Outcome value={r.outcome} /></td>
                                        <td>{r.invoice_number || '—'}</td>
                                        <td className="v3-inv-num">
                                            {r.total_minor != null ? formatMinor(r.total_minor, currency) : '—'}
                                        </td>
                                        <td className="v3-inv-muted">{r.message || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="v3-inv-modal-actions">
                        <button type="button" className="v3-inv-btn" onClick={() => { setStep('period'); setPreview(null); setRun(null); setSelected(new Set()); setTenantFilter(''); }}>
                            {t('Generate another period')}
                        </button>
                        <button
                            type="button"
                            className="v3-inv-btn v3-inv-btn-primary"
                            onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing`)}
                        >
                            {t('View invoices')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="v3-inv-kpi">
            <div className="v3-inv-kpi-label">{label}</div>
            <div className="v3-inv-kpi-value">{value}</div>
        </div>
    );
}

function Outcome({ value }) {
    const { t } = useTranslation();
    const map = {
        created: [CheckCircle, 'var(--v3-green)', t('Created')],
        replaced: [CheckCircle, 'var(--v3-accent)', t('Replaced')],
        skipped: [AlertTriangle, 'var(--v3-text-muted)', t('Skipped')],
        failed: [XCircle, 'var(--v3-red)', t('Failed')],
    };
    const [Icon, colour, label] = map[value] || [AlertTriangle, 'var(--v3-text-muted)', value];
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: colour }}>
            <Icon size={13} /> {label}
        </span>
    );
}

/** Previous calendar month as YYYY-MM — the period you almost always want. */
function defaultPeriod() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
